import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useSubsidyEligibilityStore } from "../store/subsidyEligibilityStore";
import { useAIContext } from "../store/aiContextStore";
import LoadingSpinner from "../components/common/LoadingSpinner";
import ErrorBanner from "../components/common/ErrorBanner";
import { useKeyboardShortcuts } from "../hooks/useKeyboardShortcuts";
import { importJSON } from "../utils/ImportJSON";

/**
 * EU Cross-Sovereign Subsidy Eligibility Engine (v1)
 * 
 * UI for managing subsidy eligibility scoring, grant reduction calculations,
 * and escalation path determination.
 */
export default function SubsidyEligibility() {
  const { t } = useTranslation();
  const dark = true; // Always dark mode
  const {
    tribunalDecision,
    chainIntegrityScore,
    meshIntegrity,
    passportLevel,
    corruptionSignals,
    geopoliticalWeight,
    subsidyEligibilityScore,
    grantReductionPercent,
    riskOfMisuseFlag,
    escalationPath,
    setTribunalDecision,
    setChainIntegrityScore,
    setMeshIntegrity,
    setPassportLevel,
    setCorruptionSignals,
    setGeopoliticalWeight,
    computeEligibility,
    exportEligibilityEnvelope,
    getBaseScore,
    getPassportBonus,
    getCorruptionPenalty,
    getGeoPenalty,
    getTribunalPenalty,
  } = useSubsidyEligibilityStore();

  // Derived values
  const baseScore = getBaseScore();
  const passportBonus = getPassportBonus();
  const corruptionPenalty = getCorruptionPenalty();
  const geoPenalty = getGeoPenalty();
  const tribunalPenalty = getTribunalPenalty();

  // Set active panel for AI context
  useEffect(() => {
    useAIContext.getState().setActivePanel("SubsidyEligibilityPanel");
  }, []);

  // Update AI context inputs on change
  useEffect(() => {
    useAIContext.getState().setInput("subsidyEligibilityScore", subsidyEligibilityScore);
    useAIContext.getState().setInput("grantReductionPercent", grantReductionPercent);
    useAIContext.getState().setInput("riskOfMisuseFlag", riskOfMisuseFlag);
    useAIContext.getState().setInput("escalationPath", escalationPath);
    useAIContext.getState().setInput("tribunalDecision", tribunalDecision);
    useAIContext.getState().setInput("corruptionSignals", corruptionSignals);
    useAIContext.getState().setInput("geopoliticalWeight", geopoliticalWeight);
    useAIContext.getState().setInput("passportLevel", passportLevel);
    useAIContext.getState().setInput("chainIntegrityScore", chainIntegrityScore);
    useAIContext.getState().setInput("meshIntegrity", meshIntegrity);
  }, [subsidyEligibilityScore, grantReductionPercent, riskOfMisuseFlag, escalationPath, tribunalDecision, corruptionSignals, geopoliticalWeight, passportLevel, chainIntegrityScore, meshIntegrity]);

  // Loading and error states
  const [error, setError] = useState<Error | null>(null);

  // Auto-compute on input change
  useEffect(() => {
    computeEligibility();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tribunalDecision, chainIntegrityScore, meshIntegrity, passportLevel, corruptionSignals, geopoliticalWeight]);

  const handleCompute = async () => {
    setError(null);
    try {
      await computeEligibility();
    } catch (e) {
      const error = e instanceof Error ? e : new Error(t("subsidy.computeFailed") || "Failed to compute eligibility");
      setError(error);
      console.error("Compute eligibility failed:", e);
    }
  };

  const handleExport = () => {
    setError(null);
    try {
      exportEligibilityEnvelope();
    } catch (e) {
      const error = e instanceof Error ? e : new Error(t("subsidy.exportFailed") || "Failed to export eligibility envelope");
      setError(error);
      console.error("Export failed:", e);
    }
  };

  const handleImport = async () => {
    setError(null);
    try {
      const data = await importJSON<{
        tribunalDecision?: "pending" | "warning" | "fine" | "disqualification";
        chainIntegrityScore?: number;
        meshIntegrity?: number;
        passportLevel?: "basic" | "enhanced" | "sovereign";
        corruptionSignals?: number;
        geopoliticalWeight?: number;
      }>();
      if (data) {
        store.importEligibilityEnvelope(data);
      }
    } catch (e) {
      const error = e instanceof Error ? e : new Error(t("subsidy.importFailed") || "Failed to import eligibility envelope");
      setError(error);
      console.error("Import failed:", e);
    }
  };

  // Keyboard shortcuts (after function definitions)
  useKeyboardShortcuts([
    { key: 'e', ctrl: true, action: handleExport, description: 'Export eligibility envelope' },
    { key: 'i', ctrl: true, action: handleImport, description: 'Import eligibility envelope' },
    { key: 'c', ctrl: true, action: handleCompute, description: 'Compute eligibility' },
  ]);

  return (
    <div className={`space-y-6 ${dark ? 'text-neutral-100' : 'text-gray-900'}`} style={{ minHeight: "400px" }}>
      <div className={`font-orbitron text-xl mb-4 ${dark ? 'text-evat-neon' : 'text-blue-600'}`}>
        {t("subsidy.title") || "EU Cross-Sovereign Subsidy Eligibility Engine"}
      </div>

      <ErrorBanner error={error} onRetry={() => setError(null)} showRetry={!!error} />

      {/* Input Controls */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="space-y-2">
          <label className={`text-sm ${dark ? 'text-neutral-300' : 'text-gray-700'}`}>
            {t("subsidy.tribunalDecision") || "Tribunal Decision"}
          </label>
          <select
            value={tribunalDecision}
            onChange={(e) => setTribunalDecision(e.target.value as any)}
            className={`w-full border rounded px-3 py-2 focus:outline-none ${dark ? 'bg-black border-evat-gold/40 text-evat-neon focus:border-evat-gold' : 'bg-white border-gray-300 text-gray-900 focus:border-blue-500'}`}
          >
            <option value="pending">Pending</option>
            <option value="warning">Warning</option>
            <option value="fine">Fine</option>
            <option value="disqualification">Disqualification</option>
          </select>
        </div>

        <div className="space-y-2">
          <label className={`text-sm ${dark ? 'text-neutral-300' : 'text-gray-700'}`}>
            {t("subsidy.chainIntegrityScore") || "Chain Integrity Score (0-100)"}
          </label>
          <input
            type="range"
            min="0"
            max="100"
            value={chainIntegrityScore}
            onChange={(e) => setChainIntegrityScore(Number(e.target.value))}
            className="w-full"
          />
          <div className={`text-xs ${dark ? 'text-evat-neon' : 'text-blue-600'}`}>{chainIntegrityScore}%</div>
        </div>

        <div className="space-y-2">
          <label className={`text-sm ${dark ? 'text-neutral-300' : 'text-gray-700'}`}>
            {t("subsidy.meshIntegrity") || "Mesh Integrity (0-100)"}
          </label>
          <input
            type="range"
            min="0"
            max="100"
            value={meshIntegrity}
            onChange={(e) => setMeshIntegrity(Number(e.target.value))}
            className="w-full"
          />
          <div className={`text-xs ${dark ? 'text-evat-neon' : 'text-blue-600'}`}>{meshIntegrity}%</div>
        </div>

        <div className="space-y-2">
          <label className={`text-sm ${dark ? 'text-neutral-300' : 'text-gray-700'}`}>
            {t("subsidy.passportLevel") || "Passport Level"}
          </label>
          <select
            value={passportLevel}
            onChange={(e) => setPassportLevel(e.target.value as any)}
            className={`w-full border rounded px-3 py-2 focus:outline-none ${dark ? 'bg-black border-evat-gold/40 text-evat-neon focus:border-evat-gold' : 'bg-white border-gray-300 text-gray-900 focus:border-blue-500'}`}
          >
            <option value="basic">Basic</option>
            <option value="enhanced">Enhanced</option>
            <option value="sovereign">Sovereign</option>
          </select>
        </div>

        <div className="space-y-2">
          <label className={`text-sm ${dark ? 'text-neutral-300' : 'text-gray-700'}`}>
            {t("subsidy.corruptionSignals") || "Corruption Signals"}
          </label>
          <input
            type="number"
            min="0"
            value={corruptionSignals}
            onChange={(e) => setCorruptionSignals(Number(e.target.value))}
            className={`w-full border rounded px-3 py-2 focus:outline-none ${dark ? 'bg-black border-evat-gold/40 text-evat-neon focus:border-evat-gold' : 'bg-white border-gray-300 text-gray-900 focus:border-blue-500'}`}
          />
        </div>

        <div className="space-y-2">
          <label className={`text-sm ${dark ? 'text-neutral-300' : 'text-gray-700'}`}>
            {t("subsidy.geopoliticalWeight") || "Geopolitical Weight (0-5)"}
          </label>
          <input
            type="number"
            min="0"
            max="5"
            step="0.1"
            value={geopoliticalWeight}
            onChange={(e) => setGeopoliticalWeight(Number(e.target.value))}
            className={`w-full border rounded px-3 py-2 focus:outline-none ${dark ? 'bg-black border-evat-gold/40 text-evat-neon focus:border-evat-gold' : 'bg-white border-gray-300 text-gray-900 focus:border-blue-500'}`}
          />
        </div>
      </div>

      {/* Action Buttons */}
      <div className="flex gap-4">
        <button
          onClick={handleCompute}
          className={`px-4 py-2 border rounded hover:transition-colors font-orbitron disabled:opacity-50 disabled:cursor-not-allowed ${
            dark 
              ? 'bg-evat-gold/20 border-evat-gold text-evat-gold hover:bg-evat-gold/30' 
              : 'bg-amber-50 border-amber-500 text-amber-700 hover:bg-amber-100'
          }`}
        >
          {t("subsidy.compute") || "Compute Eligibility"}
        </button>
      </div>

      {/* Results Display */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-6">
        <div className={`border rounded p-3 ${dark ? 'border-evat-gold/30 bg-black/50' : 'border-amber-300 bg-white shadow-sm'}`}>
          <div className={`text-xs mb-1 ${dark ? 'text-neutral-400' : 'text-gray-600'}`}>
            {t("subsidy.subsidyEligibilityScore") || "Eligibility Score"}
          </div>
          <div className={`text-lg font-orbitron ${
            subsidyEligibilityScore >= 70 ? (dark ? "text-green-400" : "text-green-600") :
            subsidyEligibilityScore >= 50 ? (dark ? "text-yellow-400" : "text-yellow-600") :
            (dark ? "text-red-400" : "text-red-600")
          }`}>
            {subsidyEligibilityScore.toFixed(1)}%
          </div>
        </div>

        <div className={`border rounded p-3 ${dark ? 'border-evat-gold/30 bg-black/50' : 'border-amber-300 bg-white shadow-sm'}`}>
          <div className={`text-xs mb-1 ${dark ? 'text-neutral-400' : 'text-gray-600'}`}>
            {t("subsidy.grantReductionPercent") || "Grant Reduction"}
          </div>
          <div className={`text-lg font-orbitron ${dark ? 'text-orange-400' : 'text-orange-600'}`}>
            {grantReductionPercent.toFixed(1)}%
          </div>
        </div>

        <div className={`border rounded p-3 ${dark ? 'border-evat-gold/30 bg-black/50' : 'border-amber-300 bg-white shadow-sm'}`}>
          <div className={`text-xs mb-1 ${dark ? 'text-neutral-400' : 'text-gray-600'}`}>
            {t("subsidy.riskOfMisuseFlag") || "Risk of Misuse"}
          </div>
          <div className={`text-lg font-orbitron ${riskOfMisuseFlag ? (dark ? "text-red-400" : "text-red-600") : (dark ? "text-green-400" : "text-green-600")}`}>
            {riskOfMisuseFlag ? "YES" : "NO"}
          </div>
        </div>

        <div className={`border rounded p-3 ${dark ? 'border-evat-gold/30 bg-black/50' : 'border-amber-300 bg-white shadow-sm'}`}>
          <div className={`text-xs mb-1 ${dark ? 'text-neutral-400' : 'text-gray-600'}`}>
            {t("subsidy.escalationPath") || "Escalation Path"}
          </div>
          <div className={`text-sm font-orbitron ${
            escalationPath === "prosecution" ? (dark ? "text-red-400" : "text-red-600") :
            escalationPath === "euAudit" ? (dark ? "text-orange-400" : "text-orange-600") :
            escalationPath === "regionalAudit" ? (dark ? "text-yellow-400" : "text-yellow-600") :
            (dark ? "text-green-400" : "text-green-600")
          }`}>
            {escalationPath.toUpperCase()}
          </div>
        </div>
      </div>

      {/* Breakdown Display */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mt-4">
        <div className={`border rounded p-3 ${dark ? 'border-evat-gold/30 bg-black/50' : 'border-amber-300 bg-white shadow-sm'}`}>
          <div className={`text-xs mb-1 ${dark ? 'text-neutral-400' : 'text-gray-600'}`}>
            {t("subsidy.baseScore") || "Base Score"}
          </div>
          <div className={`text-sm font-orbitron ${dark ? 'text-evat-neon' : 'text-blue-600'}`}>
            {baseScore.toFixed(1)}
          </div>
        </div>

        <div className={`border rounded p-3 ${dark ? 'border-evat-gold/30 bg-black/50' : 'border-amber-300 bg-white shadow-sm'}`}>
          <div className={`text-xs mb-1 ${dark ? 'text-neutral-400' : 'text-gray-600'}`}>
            {t("subsidy.passportBonus") || "Passport Bonus"}
          </div>
          <div className={`text-sm font-orbitron ${dark ? 'text-green-400' : 'text-green-600'}`}>
            +{passportBonus.toFixed(1)}
          </div>
        </div>

        <div className={`border rounded p-3 ${dark ? 'border-evat-gold/30 bg-black/50' : 'border-amber-300 bg-white shadow-sm'}`}>
          <div className={`text-xs mb-1 ${dark ? 'text-neutral-400' : 'text-gray-600'}`}>
            {t("subsidy.corruptionPenalty") || "Corruption Penalty"}
          </div>
          <div className={`text-sm font-orbitron ${dark ? 'text-red-400' : 'text-red-600'}`}>
            -{corruptionPenalty.toFixed(1)}
          </div>
        </div>

        <div className={`border rounded p-3 ${dark ? 'border-evat-gold/30 bg-black/50' : 'border-amber-300 bg-white shadow-sm'}`}>
          <div className={`text-xs mb-1 ${dark ? 'text-neutral-400' : 'text-gray-600'}`}>
            {t("subsidy.geoPenalty") || "Geo Penalty"}
          </div>
          <div className={`text-sm font-orbitron ${dark ? 'text-red-400' : 'text-red-600'}`}>
            -{geoPenalty.toFixed(1)}
          </div>
        </div>

        <div className={`border rounded p-3 ${dark ? 'border-evat-gold/30 bg-black/50' : 'border-amber-300 bg-white shadow-sm'}`}>
          <div className={`text-xs mb-1 ${dark ? 'text-neutral-400' : 'text-gray-600'}`}>
            {t("subsidy.tribunalPenalty") || "Tribunal Penalty"}
          </div>
          <div className={`text-sm font-orbitron ${dark ? 'text-red-400' : 'text-red-600'}`}>
            {tribunalPenalty.toFixed(1)}
          </div>
        </div>
      </div>

      {/* Export/Import Buttons */}
      <div className="fixed bottom-4 right-4 flex gap-3">
        <button
          onClick={handleImport}
          className={`px-4 py-2 border rounded hover:transition-colors font-orbitron disabled:opacity-50 disabled:cursor-not-allowed ${
            dark 
              ? 'bg-emerald-700/20 border-emerald-700 text-emerald-400 hover:bg-emerald-700/30' 
              : 'bg-emerald-50 border-emerald-500 text-emerald-700 hover:bg-emerald-100'
          }`}
          aria-label={t("subsidy.import") || "Import Eligibility JSON"}
        >
          {t("subsidy.import") || "Import Eligibility JSON"}
        </button>
        <button
          onClick={handleExport}
          className={`px-4 py-2 border rounded hover:transition-colors font-orbitron disabled:opacity-50 disabled:cursor-not-allowed ${
            dark 
              ? 'bg-evat-gold/20 border-evat-gold text-evat-gold hover:bg-evat-gold/30' 
              : 'bg-amber-50 border-amber-500 text-amber-700 hover:bg-amber-100'
          }`}
          aria-label={t("subsidy.export") || "Export Eligibility Envelope"}
        >
          {t("subsidy.export") || "Export Eligibility Envelope"}
        </button>
      </div>
    </div>
  );
}

